import { failHard } from '@resource-kit/core';
import type { WidgetName } from '@resource-kit/core';
import type { DataType, FieldType } from '@resource-kit/model';
import { resolveFieldWidget } from './registry';

/**
 * The members `toFilterType` actually reads (#2094).
 *
 * Widened from `EntityField<T>` so the MODEL face (`ModelField`: no `key`, a
 * different `options` shape) can be projected through the SAME resolution
 * order instead of a second hand-written copy of it. Every `EntityField`
 * satisfies this, so no existing call site changes.
 */
export interface FilterTypeSource {
  type: FieldType;
  widget?: WidgetName;
  filterType?: DataType;
  filterable?: boolean;
}

export const toFilterType = (field: FilterTypeSource): DataType | undefined => {
  // Explicit opt-out: computed / display-only columns declare `filterable:
  // false` so no filter is generated. ListView skips any column whose
  // `meta.filter.type` is undefined, so returning undefined here removes it
  // from the QueryBuilder schema entirely.
  if (field.filterable === false) return undefined;
  // LEGACY carrier: an explicit statement about THIS field outranks a
  // widget's generic default. The v2 model face has no counterpart on
  // purpose — manifest §2 lands `fields[].filterType` on `Widget.filterType`,
  // consulted next.
  if (field.filterType) return field.filterType;
  // The field's resolved widget (#1805 slice S4). This is what makes
  // `Widget.filterType` — one of the five projections — a real carrier
  // rather than a declared-but-unread member.
  //
  // Resolved against the field's REAL `type`, never `renderType`: the cell
  // widget follows `renderType` because that changes how a value is SHOWN,
  // while a filter sends an operator to the backend against the value's
  // actual shape. `resolveFieldWidget` already backfills an omitted
  // `filterType` from the type default, so the branch below is reached only
  // when the type default itself declares none.
  const widgetFilterType = resolveFieldWidget({
    fieldWidget: field.widget,
    type: field.type,
  }).filterType;
  if (widgetFilterType) return widgetFilterType;
  // The host's registry row is gone from this module (#2167 PR-3): the type
  // default arrives through `seedFieldTypeDefaults`, and every widget the host
  // seeds carries a `filterType`
  // (`packages/resource-kit-ui/react/registry/field-type-registry.tsx::FIELD_TYPE_WIDGETS`
  // copies it off the registry row). A widget that reached here declares none and had
  // no type default to borrow one from, which is a seed the host built wrong —
  // there is no honest DataType to invent for it.
  return failHard(
    `the widget resolved for field type "${field.type}" declares no filterType, and ` +
      'neither does that type’s default widget',
  );
};
